import React, { useState, useCallback, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload, FileText, Check, X, AlertCircle, Loader, Clock } from 'lucide-react';
import toast from 'react-hot-toast';
import { apiClient } from '../utils/apiClient';

interface UploadResult {
  id: string;
  filename: string;
  documentType: string;
  confidenceScore: number;
  status: string;
}

interface UploadError {
  filename: string;
  error: string;
}

interface ProgressItem {
  fileId: string;
  filename: string;
  status: 'uploading' | 'saving' | 'extracting' | 'saving_db' | 'completed' | 'error';
  progress: number;
  step: string;
  error?: string;
  documentId?: string;
  documentType?: string;
  confidenceScore?: number;
}

export const DocumentUpload: React.FC = () => {
  const [uploading, setUploading] = useState(false);
  const [results, setResults] = useState<UploadResult[]>([]);
  const [errors, setErrors] = useState<UploadError[]>([]);
  const [progressItems, setProgressItems] = useState<ProgressItem[]>([]);
  const [currentBatchId, setCurrentBatchId] = useState<string | null>(null);

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    if (acceptedFiles.length === 0) return;

    console.log(`Uploading ${acceptedFiles.length} files:`, acceptedFiles.map(f => f.name));
    
    setUploading(true);
    setResults([]);
    setErrors([]);
    setProgressItems([]);

    try {
      const formData = new FormData();
      acceptedFiles.forEach(file => {
        formData.append('documents', file);
      });

      const response = await apiClient.post('/api/documents/upload-batch', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.success && response.data.batchId) {
        setCurrentBatchId(response.data.batchId);
        toast.success(`Processing ${response.data.totalFiles} documents...`);
      }
    } catch (error: any) {
      console.error('Upload error:', error);
      toast.error('Upload failed: ' + (error.response?.data?.message || error.message));
      setUploading(false);
    }
  }, []);

  // Server-Sent Events for progress tracking
  useEffect(() => {
    if (!currentBatchId) return;

    const eventSource = new EventSource(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/documents/upload-progress/${currentBatchId}`);
    
    eventSource.onmessage = (event) => {
      const data = JSON.parse(event.data);
      
      if (data.type === 'connected') {
        console.log('Connected to progress stream');
      } else if (data.type === 'progress') {
        setProgressItems(prev => {
          const existing = prev.find(item => item.fileId === data.fileId);
          if (existing) {
            return prev.map(item => 
              item.fileId === data.fileId 
                ? { ...item, ...data }
                : item
            );
          } else {
            return [...prev, data];
          }
        });
      } else if (data.type === 'complete') {
        setResults(data.results || []);
        setErrors(data.errors || []);
        setUploading(false);
        setCurrentBatchId(null);
        
        if (data.successful > 0) {
          toast.success(`Successfully processed ${data.successful} documents`);
        }
        if (data.failed > 0) {
          toast.error(`Failed to process ${data.failed} documents`);
        }
      }
    };

    eventSource.onerror = (error) => {
      console.error('SSE error:', error);
      eventSource.close();
      setUploading(false);
      setCurrentBatchId(null);
    };

    return () => {
      eventSource.close();
    };
  }, [currentBatchId]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png']
    },
    disabled: uploading,
    multiple: true,
    maxFiles: 50
  });

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Nahrát dokumenty</h1>
        <p className="text-gray-600 mt-2">
          Nahrajte PDF, JPG nebo PNG soubory pro automatické vytěžení dat
        </p>
      </div>

      {/* Upload Area */}
      <div className="card mb-8">
        <div
          {...getRootProps()}
          className={`border-2 border-dashed rounded-lg p-12 text-center cursor-pointer transition-colors ${
            isDragActive
              ? 'border-primary-400 bg-primary-50'
              : uploading
              ? 'border-gray-200 bg-gray-50 cursor-not-allowed'
              : 'border-gray-300 hover:border-primary-400 hover:bg-primary-50'
          }`}
        >
          <input {...getInputProps()} />
          
          <Upload className={`h-12 w-12 mx-auto mb-4 ${
            isDragActive ? 'text-primary-500' : 'text-gray-400'
          }`} />
          
          {uploading ? (
            <div>
              <p className="text-lg font-medium text-gray-600">Nahrávání a zpracování...</p>
              <p className="text-sm text-gray-500 mt-2">Může to trvat několik okamžiků</p>
              <div className="mt-4">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
              </div>
            </div>
          ) : isDragActive ? (
            <div>
              <p className="text-lg font-medium text-primary-600">Přetáhněte soubory sem...</p>
              <p className="text-sm text-gray-500 mt-2">Uvolněte pro nahrání</p>
            </div>
          ) : (
            <div>
              <p className="text-lg font-medium text-gray-900">
                Přetáhněte soubory sem nebo klikněte pro výběr
              </p>
              <p className="text-sm text-gray-500 mt-2">
                Podporované formáty: PDF, JPG, PNG soubory do 10MB každý
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Real-time Progress */}
      {progressItems.length > 0 && (
        <div className="card mb-8">
          <div className="flex items-center mb-4">
            <Loader className="h-5 w-5 text-blue-500 mr-2 animate-spin" />
            <h3 className="text-lg font-medium text-gray-900">
              Zpracovávání dokumentů ({progressItems.length})
            </h3>
          </div>
          
          <div className="space-y-4">
            {progressItems.map((item) => (
              <div key={item.fileId} className="border rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-3">
                    {item.status === 'completed' ? (
                      <Check className="h-5 w-5 text-green-500" />
                    ) : item.status === 'error' ? (
                      <X className="h-5 w-5 text-red-500" />
                    ) : (
                      <Clock className="h-5 w-5 text-blue-500" />
                    )}
                    <div>
                      <p className="font-medium text-gray-900">{item.filename}</p>
                      <p className="text-sm text-gray-600">{item.step}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      item.status === 'completed' 
                        ? 'bg-green-100 text-green-800'
                        : item.status === 'error'
                        ? 'bg-red-100 text-red-800'
                        : 'bg-blue-100 text-blue-800'
                    }`}>
                      {item.status === 'completed' ? 'Dokončeno' : 
                       item.status === 'error' ? 'Chyba' : 'Probíhá'}
                    </span>
                  </div>
                </div>
                
                {/* Progress bar */}
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full transition-all duration-300 ${
                      item.status === 'completed' 
                        ? 'bg-green-500' 
                        : item.status === 'error' 
                        ? 'bg-red-500' 
                        : 'bg-blue-500'
                    }`}
                    style={{ width: `${item.progress}%` }}
                  />
                </div>
                
                {/* Additional info for completed items */}
                {item.status === 'completed' && item.documentType && (
                  <div className="mt-2 text-sm text-gray-600">
                    {item.documentType} • Jistota: {item.confidenceScore}%
                  </div>
                )}
                
                {/* Error message */}
                {item.status === 'error' && item.error && (
                  <div className="mt-2 text-sm text-red-600">
                    {item.error}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Results */}
      {(results.length > 0 || errors.length > 0) && (
        <div className="space-y-6">
          {/* Successful Uploads */}
          {results.length > 0 && (
            <div className="card">
              <div className="flex items-center mb-4">
                <Check className="h-5 w-5 text-green-500 mr-2" />
                <h3 className="text-lg font-medium text-gray-900">
                  Úspěšně nahráno ({results.length})
                </h3>
              </div>
              
              <div className="space-y-3">
                {results.map((result) => (
                  <div key={result.id} className="flex items-center justify-between p-4 bg-green-50 rounded-lg border border-green-200">
                    <div className="flex items-center space-x-3">
                      <FileText className="h-5 w-5 text-green-600" />
                      <div>
                        <p className="font-medium text-gray-900">{result.filename}</p>
                        <p className="text-sm text-gray-600">
                          {result.documentType} • Jistota: {result.confidenceScore}%
                        </p>
                      </div>
                    </div>
                    <span className="badge-success">Vytěženo</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Upload Errors */}
          {errors.length > 0 && (
            <div className="card">
              <div className="flex items-center mb-4">
                <X className="h-5 w-5 text-red-500 mr-2" />
                <h3 className="text-lg font-medium text-gray-900">
                  Chyby nahrávání ({errors.length})
                </h3>
              </div>
              
              <div className="space-y-3">
                {errors.map((error, index) => (
                  <div key={index} className="flex items-center justify-between p-4 bg-red-50 rounded-lg border border-red-200">
                    <div className="flex items-center space-x-3">
                      <AlertCircle className="h-5 w-5 text-red-600" />
                      <div>
                        <p className="font-medium text-gray-900">{error.filename}</p>
                        <p className="text-sm text-red-600">{error.error}</p>
                      </div>
                    </div>
                    <span className="badge-error">Selhalo</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Next Steps */}
          <div className="card border-blue-200 bg-blue-50">
            <div className="flex">
              <div className="flex-shrink-0">
                <AlertCircle className="h-5 w-5 text-blue-600" />
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-blue-800">Další kroky</h3>
                <div className="mt-2 text-sm text-blue-700">
                  <ul className="list-disc list-inside space-y-1">
                    <li>Zkontrolujte a validujte vytěžená data</li>
                    <li>Opravte chyby pro vytvoření golden standard dat</li>
                    <li>Po validaci mohou být dokumenty použity pro testování</li>
                  </ul>
                </div>
                <div className="mt-4">
                  <a
                    href="/documents?status=pending"
                    className="text-sm font-medium text-blue-600 hover:text-blue-500"
                  >
                    Přejít na validaci →
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};